import { useCallback, useEffect, useState } from "react";
import { Search, Download, Loader2, Monitor, Smartphone, Tablet } from "lucide-react";
import { adminApi, API, getToken } from "../adminApi";

const DeviceIcon = ({ device }) =>
  device === "Mobile" ? <Smartphone size={14} /> : device === "Tablet" ? <Tablet size={14} /> : <Monitor size={14} />;

export default function AdminVisitors() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/visitors", { params: { search, start, end, limit: 200 } });
      setItems(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [search, start, end]);

  useEffect(() => {
    const id = setTimeout(load, 300);
    return () => clearTimeout(id);
  }, [load]);

  const exportCsv = async () => {
    const { data } = await adminApi.get("/admin/visitors/export", {
      params: { search, start, end }, responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = "visitors.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="admin-visitors">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#0A2342]">Visitors</h1>
          <p className="text-sm text-[#4B5563] mt-1">{total} page views tracked.</p>
        </div>
        <button data-testid="visitors-export" onClick={exportCsv} className="inline-flex items-center gap-2 rounded-lg bg-[#0A2342] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#1E88E5] transition-colors">
          <Download size={16} /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] p-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]" />
          <input data-testid="visitors-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search country, city, page, browser, IP…"
            className="w-full rounded-lg border border-[#E5E7EB] pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#1E88E5]" />
        </div>
        <input type="date" data-testid="visitors-start" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-lg border border-[#E5E7EB] px-3 py-2.5 text-sm text-[#4B5563]" />
        <input type="date" data-testid="visitors-end" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded-lg border border-[#E5E7EB] px-3 py-2.5 text-sm text-[#4B5563]" />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-x-auto">
        {loading ? (
          <div className="p-8 flex items-center gap-2 text-[#4B5563]"><Loader2 className="animate-spin" size={18} /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-[#4B5563] text-sm">No visitors found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-[#4B5563] border-b border-[#E5E7EB]">
                <th className="px-4 py-3">Date & Time</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3">Device</th>
                <th className="px-4 py-3">Browser / OS</th>
                <th className="px-4 py-3">Page</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">IP</th>
              </tr>
            </thead>
            <tbody>
              {items.map((v) => (
                <tr key={v.id} className="border-b border-[#F4F6F8] hover:bg-[#F4F6F8]/50">
                  <td className="px-4 py-3 whitespace-nowrap text-[#0A2342]">{new Date(v.ts).toLocaleString()}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{v.city}, {v.country}</td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5 text-[#4B5563]"><DeviceIcon device={v.device} /> {v.device}</span></td>
                  <td className="px-4 py-3 text-[#4B5563] whitespace-nowrap">{v.browser} / {v.os}</td>
                  <td className="px-4 py-3 text-[#0A2342] font-medium">{v.path}</td>
                  <td className="px-4 py-3"><span className="text-xs rounded px-2 py-0.5 bg-[#F4F6F8] text-[#4B5563]">{v.source}</span></td>
                  <td className="px-4 py-3 text-[#4B5563] text-xs">{v.ip}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
